/**
 * AI Studio — frontend API client for the universal conversation shell.
 *
 * Targets `/api/v1/ai-studio`. Unlike AI planning, AI Studio conversations are
 * always tenant-scoped and authenticated: there is no anonymous device identity.
 *
 * The backend intentionally exposes snake_case persistence rows in a standard
 * `{ success, data }` envelope. This module is the contract boundary that maps
 * those rows to camelCase frontend models.
 */

import api from './api';
import { readConversationTitle } from '@/components/ai-studio/conversation/conversationDisplayName';
import type {
  AiStudioContextType,
  AiStudioConversation,
  AiStudioConversationSummary,
  ConversationContextRef,
  ConversationMessage,
  ConversationSendResult,
} from '@/types/aiStudio';

const STUDIO_BASE = '/ai-studio';

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface ConversationRow {
  id: string;
  tenant_id: string;
  run_id: string | null;
  binding_id: string | null;
  context_type: AiStudioContextType;
  status: AiStudioConversation['status'];
  context_ref: ConversationContextRef | null;
  shell_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

interface ConversationMessageRow {
  id: string;
  role: ConversationMessage['role'];
  content: string;
  tokens_used: number | null;
  created_at: string;
  metadata?: Record<string, unknown> | null;
}

interface SendMessageData {
  userMessage: ConversationMessageRow;
  assistantMessage: ConversationMessageRow;
  tokensUsed: number;
}

export interface CreateConversationRequest {
  contextType: AiStudioContextType;
  /** Required for AGENT_RUN conversations bound to an existing run. */
  runId?: string | null;
  bindingId?: string | null;
  contextRef?: ConversationContextRef;
  shellMetadata?: Record<string, unknown>;
  /** Archive the open Operations thread and start a blank one. */
  forceNew?: boolean;
}

function mapChoices(metadata: Record<string, unknown> | null | undefined): ConversationMessage['choices'] {
  const raw = metadata?.['choices'];
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const choices = raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const row = item as Record<string, unknown>;
      const id = typeof row['id'] === 'string' ? row['id'] : '';
      const label = typeof row['label'] === 'string' ? row['label'] : '';
      const sendText = typeof row['sendText'] === 'string' ? row['sendText'] : '';
      if (!id || !label || !sendText) return null;
      return { id, label, sendText };
    })
    .filter((c): c is { id: string; label: string; sendText: string } => c != null);
  return choices.length ? choices : undefined;
}

function mapConversation(row: ConversationRow): AiStudioConversation {
  const contextRef = row.context_ref ?? {};
  const shellMetadata = row.shell_metadata ?? {};
  const fallbackLabel = typeof contextRef.label === 'string' ? contextRef.label : null;
  const displayTitle = readConversationTitle(shellMetadata, fallbackLabel);
  return {
    id: row.id,
    tenantId: row.tenant_id,
    runId: row.run_id,
    bindingId: row.binding_id,
    contextType: row.context_type,
    status: row.status,
    contextRef,
    shellMetadata,
    name: displayTitle || null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapMessage(row: ConversationMessageRow): ConversationMessage {
  const metadata = row.metadata ?? undefined;
  const authorName =
    metadata && typeof metadata['author_name'] === 'string' ? metadata['author_name'] : null;
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    createdAt: row.created_at,
    tokensUsed: row.tokens_used,
    choices: mapChoices(metadata),
    authorName,
  };
}

export async function listConversations(filter?: {
  contextType?: AiStudioContextType;
  runId?: string;
}): Promise<AiStudioConversationSummary[]> {
  const response = await api.get<ApiEnvelope<ConversationRow[]>>(`${STUDIO_BASE}/conversations`);

  // The current backend list endpoint is tenant-wide, so apply the host's explicit
  // context/run scope after mapping until server-side filters are introduced.
  return response.data.data
    .map(mapConversation)
    .filter(
      (conversation) =>
        (!filter?.contextType || conversation.contextType === filter.contextType) &&
        (!filter?.runId || conversation.runId === filter.runId)
    )
    .map((conversation) => ({
      id: conversation.id,
      name: conversation.name,
      contextType: conversation.contextType,
      status: conversation.status,
      runId: conversation.runId,
      bindingId: conversation.bindingId,
      shellMetadata: conversation.shellMetadata,
      createdAt: conversation.createdAt,
      updatedAt: conversation.updatedAt,
    }));
}

export async function getConversation(conversationId: string): Promise<AiStudioConversation> {
  const response = await api.get<ApiEnvelope<ConversationRow>>(
    `${STUDIO_BASE}/conversations/${conversationId}`
  );
  return mapConversation(response.data.data);
}

export async function listConversationMessages(
  conversationId: string
): Promise<ConversationMessage[]> {
  const response = await api.get<ApiEnvelope<ConversationMessageRow[]>>(
    `${STUDIO_BASE}/conversations/${conversationId}/messages`
  );
  return response.data.data.map(mapMessage);
}

export async function createConversation(
  request: CreateConversationRequest
): Promise<AiStudioConversation> {
  if (request.contextType === 'AGENT_RUN' && !request.runId) {
    throw new Error('AGENT_RUN conversations require a runId.');
  }

  const response = await api.post<ApiEnvelope<ConversationRow>>(
    `${STUDIO_BASE}/conversations`,
    {
      context_type: request.contextType,
      run_id: request.runId ?? null,
      binding_id: request.bindingId ?? null,
      context_ref: request.contextRef ?? {},
      shell_metadata: request.shellMetadata ?? {},
      force_new: request.forceNew === true,
    }
  );
  return mapConversation(response.data.data);
}

export async function renameConversation(
  conversationId: string,
  title: string
): Promise<AiStudioConversation> {
  const response = await api.patch<ApiEnvelope<ConversationRow>>(
    `${STUDIO_BASE}/conversations/${conversationId}`,
    { title: title.trim() }
  );
  return mapConversation(response.data.data);
}

/** Send one turn through the backend's current non-streaming LLM endpoint. */
export async function sendConversationMessage(
  conversationId: string,
  content: string,
  options?: {
    modelConfigurationId?: string | null;
    pageBind?: { route?: string; tab?: string; selectionId?: string | null } | null;
  }
): Promise<ConversationSendResult> {
  const body: {
    content: string;
    model_configuration_id?: string;
    page_bind?: { route?: string; tab?: string; selectionId?: string | null };
  } = { content };
  if (options?.modelConfigurationId) {
    body.model_configuration_id = options.modelConfigurationId;
  }
  if (options?.pageBind) {
    body.page_bind = options.pageBind;
  }
  const response = await api.post<ApiEnvelope<SendMessageData>>(
    `${STUDIO_BASE}/conversations/${conversationId}/messages/send`,
    body,
    { timeout: 120000 }
  );
  return {
    userMessage: mapMessage(response.data.data.userMessage),
    assistantMessage: mapMessage(response.data.data.assistantMessage),
    tokensUsed: response.data.data.tokensUsed,
  };
}

/** Generic seam so a future real streaming transport can be added without changing the shell. */
export interface ConversationTransport {
  send: typeof sendConversationMessage;
}

export const aiStudioConversationTransport: ConversationTransport = {
  send: sendConversationMessage,
};
